package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * How the frontend should render a preview.
 *
 * Every value is declared even though ADV-1200 wires only [TEXT], so the
 * frontend's dispatch is exhaustive and each later renderer subtask lands as a
 * compile error to resolve rather than a silent fallthrough. A renderer the
 * backend policy does not yet emit simply never reaches the client.
 */
@Serializable
enum class PreviewRenderer {
    TEXT,
    TABULAR,
    IMAGE,
    PDF,
    RASTER,

    /** ADV-1228. An archive's entry listing, drawn as a folder tree. The payload is [ArchiveListingDto]. */
    ARCHIVE,
    UNSUPPORTED,
}

/**
 * Whether a preview can be shown, and if not, why.
 *
 * All three non-[RENDER] outcomes fall back to a download action in the UI, but
 * they carry different messages: ADV-1200 requires an over-ceiling file to say
 * so rather than looking indistinguishable from an unsupported type.
 */
@Serializable
enum class PreviewOutcome {
    /** Renderable. [PreviewDescriptorDto.renderer] says how. */
    RENDER,

    /** Renderable in principle, but past this format's refuse-ceiling. */
    TOO_LARGE,

    /** No renderer claims this extension or stored type. Folders land here too. */
    UNSUPPORTED_TYPE,

    /** Still uploading, or mid-pull — there are no committed bytes to read yet. */
    NOT_READY,
}

/**
 * What the viewer shell needs to decide what to draw, before it fetches a byte.
 *
 * [truncated] here is advisory. It is exact when the file's size is known and
 * optimistic when it is not (a mid-pull row has a null size). The
 * `X-Drive-Preview-Truncated` header on `/preview/content` is authoritative,
 * because that read derives truncation from the bytes that actually arrived.
 */
@Serializable
data class PreviewDescriptorDto(
    val fileId: String,
    val displayName: String,
    val outcome: PreviewOutcome,
    /** Meaningful only when [outcome] is [PreviewOutcome.RENDER]. */
    val renderer: PreviewRenderer,
    /** Type resolved by policy, extension-first. Null when nothing resolved. */
    val mimeType: String?,
    /** Whole-file size, or null when not yet known. */
    val sizeBytes: Long?,
    /**
     * How many bytes of the FILE `/preview/content` will read. 0 unless
     * [outcome] is [PreviewOutcome.RENDER].
     *
     * For [PreviewRenderer.IMAGE] the response body is a downscaled rendition,
     * so it is usually smaller than this. The `X-Drive-Preview-Rendition`
     * header on the response reports that.
     */
    val previewBytes: Long,
    /** True when fewer than [sizeBytes] bytes will be served — the UI must say so. */
    val truncated: Boolean,
    /** User-facing reason when [outcome] is not [PreviewOutcome.RENDER]. */
    val message: String? = null,
    /**
     * True when the server BUILDS the payload from the file rather than
     * sending the file's own bytes — a downscaled image rendition, or the
     * first sheet of a workbook as csv.
     *
     * A caller that only wants a peek must not ask for one of these. Building
     * it reads the whole source and parses it, and the picker's tooltip fires
     * on hover, so a pointer crossing a folder of workbooks would convert every
     * one of them. The renderer cannot answer this: TABULAR covers both a csv,
     * which is cheap, and a workbook, which is not.
     */
    val derived: Boolean = false,
)

/**
 * ADV-1228. The entry listing of an archive, as served by `/preview/content`
 * for [PreviewRenderer.ARCHIVE].
 *
 * Built on the server from the archive's central directory, which is read with
 * ranged reads and never decompressed. At most `drive.preview.archive-directory-ceiling-bytes`
 * of the directory is read, so a large archive is listed in part.
 */
@Serializable
data class ArchiveListingDto(
    /** Entries the archive DECLARES. Checked against the directory size, but still the archive's claim. */
    val totalEntries: Long,
    /**
     * True when the directory outran the ceiling and only the first records were
     * listed. Never silent: the pane states "Showing N of M entries".
     */
    val truncated: Boolean,
    /** In central-directory order. The client sorts. */
    val entries: List<ArchiveEntryDto>,
)

/** One entry of an [ArchiveListingDto]. Every field is display data, never a path to act on. */
@Serializable
data class ArchiveEntryDto(
    /**
     * Sanitised segments joined by `/`, with no leading or trailing `/`. For an
     * [unsafePath] entry it is the whole sanitised raw name, which the client
     * must NOT split into folders.
     */
    val path: String,
    /** The raw name ended with `/` or `\`. */
    val directory: Boolean = false,
    /** Uncompressed size as DECLARED by the archive. 0 for a directory. */
    val size: Long = 0,
    /** Password-protected. Listed, not refused. */
    val encrypted: Boolean = false,
    /** The raw name is not a safe relative path (`..`, absolute, drive letter, control character). */
    val unsafePath: Boolean = false,
)

/**
 * The one codec for [ArchiveListingDto], used by the server to encode and by the
 * browser to decode, so the two sides cannot drift on names or defaults.
 */
object ArchiveListingJson {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    fun encode(listing: ArchiveListingDto): String = json.encodeToString(ArchiveListingDto.serializer(), listing)

    fun decode(text: String): ArchiveListingDto = json.decodeFromString(ArchiveListingDto.serializer(), text)
}
